import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart } from 'react-google-charts';
import styles from './NotificationAnalytics.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function Pagination({ currentPage, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange, t }) {
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    const getPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }
        return pages;
    };

    if (totalItems === 0) return null;

    const showingText = t('NSNotificationAnalytics.showing', 'Показано');
    const ofText = t('NSNotificationAnalytics.of', 'из');

    return (
        <div className={styles.pagination}>
            <div className={styles.paginationInfo}>
                {showingText} {startItem}-{endItem} {ofText} {totalItems}
            </div>
            <div className={styles.paginationControls}>
                <select
                    className={styles.pageSizeSelect}
                    value={pageSize}
                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                >
                    {PAGE_SIZE_OPTIONS.map(size => (
                        <option key={size} value={size}>{size}</option>
                    ))}
                </select>
                <button
                    className={styles.paginationButton}
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    &lt;
                </button>
                {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                        <span key={`ellipsis-${index}`} className={styles.paginationEllipsis}>...</span>
                    ) : (
                        <button
                            key={page}
                            className={`${styles.paginationButton} ${currentPage === page ? styles.paginationButtonActive : ''}`}
                            onClick={() => onPageChange(page)}
                        >
                            {page}
                        </button>
                    )
                ))}
                <button
                    className={styles.paginationButton}
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    &gt;
                </button>
            </div>
        </div>
    );
}

function Tooltip({ text }) {
    return (
        <span className={styles.tooltipWrapper}>
            <span className={styles.tooltipIcon}>?</span>
            <span className={styles.tooltipText}>{text}</span>
        </span>
    );
}

function formatDate(timestamp) {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function formatMoney(amount) {
    if (!amount && amount !== 0) return '-';
    return amount.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ₸';
}

export default function NotificationAnalytics({ data, isLoading }) {
    const { t } = useTranslation();
    const [showOnlyReturned, setShowOnlyReturned] = useState(false);

    // Pagination state for logs
    const [logsPage, setLogsPage] = useState(1);
    const [logsPageSize, setLogsPageSize] = useState(10);

    // Pagination state for users
    const [usersPage, setUsersPage] = useState(1);
    const [usersPageSize, setUsersPageSize] = useState(20);

    // Calculate analytics from raw data
    const analytics = useMemo(() => {
        const users = data?.users || [];
        const logStats = data?.log_stats || null;
        const deliveryStats = data?.delivery_stats || null;

        // Filter only users who actually received a notification (last_auto_notification_date > 0)
        const usersWithNotification = users.filter(user =>
            user.last_auto_notification_date && user.last_auto_notification_date > 0
        );

        const totalNotifications = usersWithNotification.length;

        // Determine returned users: those who made a transaction AFTER receiving notification
        const returnedUsers = usersWithNotification.filter(user =>
            user.last_transaction_date &&
            user.last_transaction_date > user.last_auto_notification_date
        );

        const totalReturned = returnedUsers.length;
        const returnRate = totalNotifications > 0 ? (totalReturned / totalNotifications) * 100 : 0;

        // Calculate return time for each returned user (in days)
        const returnTimes = returnedUsers.map(user => {
            const timeDiff = user.last_transaction_date - user.last_auto_notification_date;
            return timeDiff / (1000 * 60 * 60 * 24); // Convert ms to days
        });

        const averageReturnDays = returnTimes.length > 0
            ? returnTimes.reduce((sum, days) => sum + days, 0) / returnTimes.length
            : 0;

        // Calculate total sum from returned users
        const totalReturnSum = returnedUsers.reduce((sum, user) => sum + (user.last_transaction_sum || 0), 0);

        // Return time distribution
        const distribution = { '0-1': 0, '1-3': 0, '3-7': 0, '7-14': 0, '14+': 0 };
        returnTimes.forEach(days => {
            if (days <= 1) distribution['0-1']++;
            else if (days <= 3) distribution['1-3']++;
            else if (days <= 7) distribution['3-7']++;
            else if (days <= 14) distribution['7-14']++;
            else distribution['14+']++;
        });

        // Add return time data to users (only those who received notification)
        const usersWithReturnTime = usersWithNotification.map(user => {
            let returnDays = null;
            const isReturned = user.last_transaction_date &&
                user.last_transaction_date > user.last_auto_notification_date;

            if (isReturned) {
                returnDays = (user.last_transaction_date - user.last_auto_notification_date) / (1000 * 60 * 60 * 24);
            }

            return {
                ...user,
                returnDays,
                isReturned,
            };
        });

        return {
            totalNotifications,
            totalReturned,
            returnRate,
            averageReturnDays,
            totalReturnSum,
            distribution,
            users: usersWithReturnTime,
            logStats,
            deliveryStats,
        };
    }, [data]);

    if (isLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loading}>{t('loading') || 'Загрузка...'}</div>
            </div>
        );
    }

    const hasData = analytics.totalNotifications > 0 || (analytics.logStats?.total_logs > 0) || (analytics.deliveryStats?.total > 0);

    if (!hasData) {
        return (
            <div className={styles.container}>
                <h1 className={styles.title}>{t('NSNotificationAnalytics.title') || 'Аналитика авторассылок'}</h1>
                <div className={styles.noData}>{t('NSNotificationAnalytics.noData') || 'Нет данных по авторассылкам'}</div>
            </div>
        );
    }

    // Prepare return time distribution chart
    const returnTimeData = [
        [t('NSNotificationAnalytics.range') || 'Период', t('NSNotificationAnalytics.users') || 'Пользователей'],
        [`0-1 ${t('NSNotificationAnalytics.days') || 'дн'}`, analytics.distribution['0-1']],
        [`1-3 ${t('NSNotificationAnalytics.days') || 'дн'}`, analytics.distribution['1-3']],
        [`3-7 ${t('NSNotificationAnalytics.days') || 'дн'}`, analytics.distribution['3-7']],
        [`7-14 ${t('NSNotificationAnalytics.days') || 'дн'}`, analytics.distribution['7-14']],
        [`14+ ${t('NSNotificationAnalytics.days') || 'дн'}`, analytics.distribution['14+']],
    ];

    const displayedUsers = showOnlyReturned
        ? analytics.users.filter(u => u.isReturned)
        : analytics.users;

    // Paginated logs
    const allLogs = analytics.logStats?.recent_logs || [];
    const totalLogsPages = Math.ceil(allLogs.length / logsPageSize);
    const paginatedLogs = allLogs.slice((logsPage - 1) * logsPageSize, logsPage * logsPageSize);

    // Paginated users
    const totalUsersPages = Math.ceil(displayedUsers.length / usersPageSize);
    const paginatedUsers = displayedUsers.slice((usersPage - 1) * usersPageSize, usersPage * usersPageSize);

    // Reset users page when filter changes
    const handleFilterChange = (checked) => {
        setShowOnlyReturned(checked);
        setUsersPage(1);
    };

    // Handle page size changes (reset to page 1)
    const handleLogsPageSizeChange = (newSize) => {
        setLogsPageSize(newSize);
        setLogsPage(1);
    };

    const handleUsersPageSizeChange = (newSize) => {
        setUsersPageSize(newSize);
        setUsersPage(1);
    };

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>{t('NSNotificationAnalytics.title') || 'Аналитика авторассылок'}</h1>

            {analytics.deliveryStats && (
                <div className={styles.cardsRow}>
                    <div className={styles.card}>
                        <div className={styles.cardValue}>{analytics.deliveryStats.total || 0}</div>
                        <div className={styles.cardLabel}>{t('NSNotificationAnalytics.deliveryTotal') || 'Всего'}</div>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardValue}>{analytics.deliveryStats.sent || 0}</div>
                        <div className={styles.cardLabel}>{t('NSNotificationAnalytics.deliverySent') || 'Отправлено'}</div>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardValue}>{analytics.deliveryStats.delivered || 0}</div>
                        <div className={styles.cardLabel}>{t('NSNotificationAnalytics.deliveryDelivered') || 'Доставлено'}</div>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardValue}>{analytics.deliveryStats.read || 0}</div>
                        <div className={styles.cardLabel}>{t('NSNotificationAnalytics.deliveryRead') || 'Прочитано'}</div>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardValueSecondary}>{analytics.deliveryStats.failed || 0}</div>
                        <div className={styles.cardLabel}>{t('NSNotificationAnalytics.deliveryFailed') || 'Ошибки'}</div>
                    </div>
                </div>
            )}

            {/* Summary Cards */}
            <div className={styles.cardsRow}>
                {analytics.logStats && (
                    <div className={styles.card}>
                        <div className={styles.cardValue}>{analytics.logStats.total_logs || 0}</div>
                        <div className={styles.cardLabel}>
                            {t('NSNotificationAnalytics.totalRuns') || 'Запусков рассылки'}
                            <Tooltip text={t('NSNotificationAnalytics.totalRunsTooltip') || 'Сколько раз система запускала авторассылку'} />
                        </div>
                    </div>
                )}
                <div className={styles.card}>
                    <div className={styles.cardValue}>{analytics.totalNotifications}</div>
                    <div className={styles.cardLabel}>
                        {t('NSNotificationAnalytics.totalSent') || 'Уникальных получателей'}
                        <Tooltip text={t('NSNotificationAnalytics.totalSentTooltip') || 'Количество уникальных клиентов, получивших авторассылку'} />
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardValue}>{analytics.totalReturned}</div>
                    <div className={styles.cardLabel}>
                        {t('NSNotificationAnalytics.returnedUsers') || 'Вернувшихся'}
                        <Tooltip text={t('NSNotificationAnalytics.returnedUsersTooltip') || 'Клиенты, совершившие покупку после получения авторассылки'} />
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardValue}>{analytics.returnRate.toFixed(1)}%</div>
                    <div className={styles.cardLabel}>
                        {t('NSNotificationAnalytics.returnRate') || 'Процент возврата'}
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardValueSecondary}>{analytics.averageReturnDays.toFixed(1)}</div>
                    <div className={styles.cardLabel}>
                        {t('NSNotificationAnalytics.avgReturnDays') || 'Среднее время возврата (дней)'}
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardValueSecondary}>{formatMoney(analytics.totalReturnSum)}</div>
                    <div className={styles.cardLabel}>
                        {t('NSNotificationAnalytics.totalReturnSum') || 'Сумма покупок вернувшихся'}
                    </div>
                </div>
            </div>

            {/* Return Time Distribution Chart */}
            {analytics.totalReturned > 0 && (
                <div className={styles.chartsRow}>
                    <div className={styles.chartCard}>
                        <h3 className={styles.chartTitle}>{t('NSNotificationAnalytics.returnTimeDistribution') || 'Распределение по времени возврата'}</h3>
                        <Chart
                            chartType="PieChart"
                            width="100%"
                            height="300px"
                            data={returnTimeData}
                            options={{
                                legend: { position: 'right', textStyle: { color: '#666' } },
                                chartArea: { width: '80%', height: '80%', backgroundColor: '#fff' },
                                backgroundColor: '#fff',
                                colors: ['#73D389', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800'],
                                pieHole: 0.4,
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Recent Notification Logs */}
            {allLogs.length > 0 && (
                <div className={styles.tableCard}>
                    <h3 className={styles.tableTitle}>{t('NSNotificationAnalytics.recentLogs') || 'История рассылок'}</h3>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{t('NSNotificationAnalytics.date') || 'Дата'}</th>
                                <th>{t('NSNotificationAnalytics.phonesSent') || 'Отправлено'}</th>
                                <th>{t('NSNotificationAnalytics.status') || 'Статус'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedLogs.map((log, index) => (
                                <tr key={log.date || index}>
                                    <td>{(logsPage - 1) * logsPageSize + index + 1}</td>
                                    <td>{formatDate(log.date)}</td>
                                    <td>{log.phones?.length || 0}</td>
                                    <td>
                                        <span className={log.global_error ? styles.statusInProgress : styles.statusCompleted}>
                                            {log.global_error
                                                ? (t('NSNotificationAnalytics.error') || 'Ошибка')
                                                : (t('NSNotificationAnalytics.success') || 'Успешно')}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <Pagination
                        currentPage={logsPage}
                        totalPages={totalLogsPages}
                        totalItems={allLogs.length}
                        pageSize={logsPageSize}
                        onPageChange={setLogsPage}
                        onPageSizeChange={handleLogsPageSizeChange}
                        t={t}
                    />
                </div>
            )}

            {/* Users Table */}
            {analytics.users.length > 0 && (
                <div className={styles.tableCard}>
                    <div className={styles.tableHeader}>
                        <h3 className={styles.tableTitle}>{t('NSNotificationAnalytics.usersList') || 'Список клиентов'}</h3>
                        <label className={styles.filterCheckbox}>
                            <input
                                type="checkbox"
                                checked={showOnlyReturned}
                                onChange={(e) => handleFilterChange(e.target.checked)}
                            />
                            {t('NSNotificationAnalytics.showOnlyReturned') || 'Только вернувшиеся'}
                        </label>
                    </div>
                    {displayedUsers.length > 0 ? (
                        <>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{t('NSNotificationAnalytics.name') || 'Имя'}</th>
                                        <th>{t('NSNotificationAnalytics.phone') || 'Телефон'}</th>
                                        <th>{t('NSNotificationAnalytics.notificationDate') || 'Дата рассылки'}</th>
                                        <th>{t('NSNotificationAnalytics.transactionDate') || 'Дата покупки'}</th>
                                        <th>{t('NSNotificationAnalytics.transactionSum') || 'Сумма покупки'}</th>
                                        <th>{t('NSNotificationAnalytics.returnDays') || 'Дней до возврата'}</th>
                                        <th>{t('NSNotificationAnalytics.status') || 'Статус'}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedUsers.map((user, index) => (
                                        <tr key={user.id}>
                                            <td>{(usersPage - 1) * usersPageSize + index + 1}</td>
                                            <td>{[user.firstname, user.lastname].filter(Boolean).join(' ') || '-'}</td>
                                            <td>{user.phone || '-'}</td>
                                            <td>{formatDate(user.last_auto_notification_date)}</td>
                                            <td>{formatDate(user.last_transaction_date)}</td>
                                            <td>{formatMoney(user.last_transaction_sum)}</td>
                                            <td>{user.returnDays !== null ? user.returnDays.toFixed(1) : '-'}</td>
                                            <td>
                                                <span className={user.isReturned ? styles.statusCompleted : styles.statusInProgress}>
                                                    {user.isReturned
                                                        ? (t('NSNotificationAnalytics.returned') || 'Вернулся')
                                                        : (t('NSNotificationAnalytics.notReturned') || 'Не вернулся')}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <Pagination
                                currentPage={usersPage}
                                totalPages={totalUsersPages}
                                totalItems={displayedUsers.length}
                                pageSize={usersPageSize}
                                onPageChange={setUsersPage}
                                onPageSizeChange={handleUsersPageSizeChange}
                                t={t}
                            />
                        </>
                    ) : (
                        <div className={styles.noData}>{t('NSNotificationAnalytics.noFilteredUsers') || 'Нет пользователей по фильтру'}</div>
                    )}
                </div>
            )}
        </div>
    );
}
